import {
  ESkillActivationResult,
  ESkillActivationType,
  type TSkillActivationRecord,
} from "../types/skills";
import {
  ESkillActivationTimelineKind,
  type ISkillActivationTimelineItem,
  type TSkillTimelineMessageKey,
} from "./skill-activation-timeline.types";

// NOTE: The key for the single unavailable row. A literal rather than a uuid,
// because the row stands for every refused pick at once and so has no one skill to
// key off; it cannot collide with a loaded row, whose key is always a uuid.
const UNAVAILABLE_ITEM_KEY = "unavailable";

// NOTE: NSAI-3308 — the one place a message's durable activation records become
// renderable rows, shared so web and mobile cannot answer the same question
// differently. Three rules, none of which a platform may re-decide:
//
// 1. Order is the server's `position`, never arrival order, so a message reads the
//    same live, after a replayed event and after a refetch.
// 2. `UNAVAILABLE` collapses to ONE row however many pinned skills the runtime
//    refused. The result vocabulary is deliberately coarse — a client cannot tell
//    deleted from private from disabled from re-versioned — so one sentence per
//    refused uuid would imply a precision that does not exist, and the count alone
//    is already more than the user is owed.
// 3. The name is the stored historical name, taken as plain text. Nothing here
//    looks up a skill's CURRENT name, which is what keeps old history readable
//    after a rename, an archive or a delete.
//
// A `LOADED` record carrying no name is dropped rather than rendered. The server
// always stores a name for a successful activation, so this is an anomaly path;
// dropping it is the only option that does not state something untrue — an empty
// label reads as a bug, and the "no longer available" sentence would be a lie
// about a skill that did load.
export const buildSkillActivationTimeline = (
  activations: readonly TSkillActivationRecord[] | undefined,
): ISkillActivationTimelineItem[] => {
  if (!activations || activations.length === 0) {
    return [];
  }

  const ordered = [...activations].sort(
    (left, right) => left.position - right.position,
  );

  const items: ISkillActivationTimelineItem[] = [];
  let hasUnavailableItem = false;

  for (const record of ordered) {
    if (record.result === ESkillActivationResult.UNAVAILABLE) {
      if (hasUnavailableItem) {
        continue;
      }
      hasUnavailableItem = true;
      items.push({
        key: UNAVAILABLE_ITEM_KEY,
        kind: ESkillActivationTimelineKind.UNAVAILABLE,
        name: null,
      });
      continue;
    }

    if (record.name === null || record.name.length === 0) {
      continue;
    }

    items.push({
      key: record.skillUuid,
      kind:
        record.activationType === ESkillActivationType.EXPLICIT
          ? ESkillActivationTimelineKind.LOADED_EXPLICIT
          : ESkillActivationTimelineKind.LOADED_AUTOMATIC,
      name: record.name,
    });
  }

  return items;
};

// NOTE: NSAI-5803 — how many rows describe a skill that ACTUALLY LOADED. The
// collapsed chip is headed "Skills (n)", and n used to be `items.length`, which
// counts the unavailable row too: a message whose only pick was refused read
// "SKILLS (1)", i.e. "one skill was used", when nothing had been loaded at all.
// You only learned otherwise by expanding it.
//
// Shared rather than computed in each client for the same reason the builder is:
// web and mobile must not disagree about what the number on a message means.
export const countLoadedSkillActivations = (
  items: readonly ISkillActivationTimelineItem[],
): number =>
  items.filter((item) => item.kind !== ESkillActivationTimelineKind.UNAVAILABLE)
    .length;

// NOTE: Which sentence a row kind renders as. Shared for the same reason the
// builder is: a message must read the same on both clients, down to the wording.
// The unavailable row deliberately has no name in it — see `buildSkillActivationTimeline`.
const TIMELINE_MESSAGE_KEY: Readonly<
  Record<ESkillActivationTimelineKind, TSkillTimelineMessageKey>
> = {
  [ESkillActivationTimelineKind.LOADED_AUTOMATIC]: "timeline.loadedAutomatic",
  [ESkillActivationTimelineKind.LOADED_EXPLICIT]: "timeline.loadedExplicit",
  [ESkillActivationTimelineKind.UNAVAILABLE]: "timeline.unavailable",
};

export const resolveSkillTimelineMessageKey = (
  kind: ESkillActivationTimelineKind,
): TSkillTimelineMessageKey => TIMELINE_MESSAGE_KEY[kind];
